import {
  Component,
  OnDestroy,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { ViewConfigGRPCService } from '../ProxyServices/view-config.grpc.service';
import { WorkorderGRPCService } from '../ProxyServices/workorder.grpc.service';
import { Observable, Subscription } from 'rxjs';
import { UserModel, WorkOrderBase } from '../CommonFiles/BusinessModels';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
  process,
} from '@progress/kendo-data-query';
import { ColumnSettings } from '../CommonFiles/UIModels';
import { MapSharedUtilService } from './services/map-shared-util.service';

import { GridBaseComponent } from '../OFramework/Grid/GridBaseComponent';
import { OConfigService } from '@olameter/o-config-service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { toSignal } from '@angular/core/rxjs-interop';
import { OAuthService } from '@olameter/o-authentication';
import { OMenuItem } from '@olameter/o-menu';
import { UserGRPCService } from '../ProxyServices/user.grpc.service';
import { LanguageService } from '../shared/services/language.service';

export enum ShowItem {
  All = 'All',
  Selected = 'Selected',
  UnSelected = 'UnSelected',
}
@Component({
  standalone: false,
  selector: 'app-home-component',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy {
  pageHeight = signal<number>(window.innerHeight);
  private resizeObserver: ResizeObserver;

  timezone: WritableSignal<string> = signal('');
  subscriptions: Subscription[] = [];
  contractName: WritableSignal<string> = signal('');
  startDateFilter: WritableSignal<Date> = signal(null);
  endDateFilter: WritableSignal<Date> = signal(null);
  installDateOption: WritableSignal<string> = signal('');
  showItem: WritableSignal<ShowItem> = signal(ShowItem.All);
  workOrders!: Signal<WorkOrderBase[]>;
  selectedWorkOrders!: Signal<WorkOrderBase[]>;
  // grid state for kendo grid
  // reset this initiate
  initialGridState: State = {
    skip: 0,
    take: 40,
    sort: [],
    group: [],
    filter: GridBaseComponent.initialFilter.filter,
  };
  gridState: WritableSignal<State> = signal(this.initialGridState);
  //add a new state for map alone cos it doesnt have to react to grid's own state
  // Grid state form server or parent.
  gridStateMap: WritableSignal<State> = signal(this.initialGridState);
  columnsList: WritableSignal<ColumnSettings[]> = signal([]);
  // this is to track initial change in cols
  canFilterColsOnChange: boolean = false;

  selectMapTool: WritableSignal<string> = signal('');
  gridData = computed<GridDataResult>(() => {
    let data = this.workOrders();
    const gridState = this.gridState();
    const showItem = this.showItem();
    const selectedSet = this.selectedSet();
    if (showItem === ShowItem.Selected) {
      data = data.filter((wo) => selectedSet.has(wo.name));
    }
    if (showItem === ShowItem.UnSelected) {
      data = data.filter((wo) => !selectedSet.has(wo.name));
    }

    let state = {
      ...gridState,
      skip: 0,
      take: data.length,
    };
    const grid = process(data, state);

    return grid;
  });

  selectedSet = computed<Set<string>>(() => {
    const selectedSet = new Set(
      this.selectedWorkOrders().map((feature) => feature.name)
    );
    return selectedSet;
  });
  menu: OMenuItem[] = [];

  users$: Observable<UserModel[]>;
  userSignalArr!: Signal<UserModel[]>;
  user = computed(() =>
    this.userSignalArr().length > 0 ? this.userSignalArr()[0] : null
  );

  environment:string = '';

  constructor(
    public authService: OAuthService,
    protected clientViewConf: ViewConfigGRPCService,
    public _clientWO: WorkorderGRPCService,
    public _mapSharedUtilService: MapSharedUtilService,
    private _config: OConfigService,
    private userService: UserGRPCService,
    public languageService: LanguageService
  ) {
    this.environment = this._config.getConfig().environment;
    this.resizeObserver = new ResizeObserver(() => {
      // Debounce updates
      setTimeout(() => {
        this.pageHeight.set(window.innerHeight);
      }, 200);
    });

    this.resizeObserver.observe(document.body);

    // Convert the Observable to a Signal
    this.workOrders = toSignal(this._clientWO, { initialValue: [] });
    this.selectedWorkOrders = toSignal(this._clientWO.RXselectedWOIDsShared, {
      initialValue: [],
    });
    this.users$ = this.userService.asObservable();
    this.userSignalArr = toSignal(this.users$, { initialValue: [] });
  }

  ngOnInit() {
    this.currentView();
    this.menu = this._config.getConfig().menu;
  }

  // listen to acitve view
  currentView() {
    const activeViewSub = this.clientViewConf.RXsubscribeToCurrentActiveView(
      async (value) => {
        if (value) {
          const toSelect = value.toSelect;
          this.timezone.set(toSelect.ContractTimeZone);
          const gridState = toSelect.GridState;
          if (gridState.filter) {
            const filter = gridState.filter;
            let filters = filter.filters.length > 0 ? filter.filters : [];
            filters = filters.map((item: FilterDescriptor) => {
              const value = Array.isArray(item.value)
                ? item.value.map((filterInnerItem: string) =>
                    this.decodeFilterValue(filterInnerItem)
                  )
                : item.value;
              return {
                ...item,
                value,
              };
            });
            filter.filters = filters;
            gridState.filter = filter;
          }
          // reset the grid filters state
          if (value.reloadDataFromBE || this._clientWO.success() === false) {
            this.gridStateMap.set(gridState); // Grid filters from the form
            const columnsConfig = toSelect.ColumnsConfig.map((item) => {
              let title = item.title;
              if (item.field === 'BlackoutRemainingDays') {
                title = 'Blackout Countdown Days';
              }
              if (item.field === 'InstallDate') {
                title = 'Planned Install Date';
              }
              return {
                ...item,
                title
              };
            });
            this.columnsList.set(columnsConfig);
            this.installDateOption.set(toSelect.installDateOption || 'All');

            this.contractName.set(toSelect.contractName);

            const oneWeekInMilliseconds = 7 * 24 * 60 * 60 * 1000;
            const nextWeek = new Date();

            nextWeek.setTime(new Date().getTime() + oneWeekInMilliseconds);
            this.endDateFilter.set(toSelect.endDateFilter || nextWeek);
            this.startDateFilter.set(toSelect.startDateFilter || new Date());

            // reset grid State on reload or on success
            const localGridState = {
              ...this.initialGridState,
              sort: gridState.sort,
            };
            this.updatLocalGridState(localGridState);
            this.updateShowItem(ShowItem.All);
            // load data from server
            setTimeout(() => {
              this.getWorkOrders(toSelect);
              this.updateWOSelection([]);
            }, 300);
          }
        }
      }
    );
    this.subscriptions.push(activeViewSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.map((sub) => sub.unsubscribe());

    this.resizeObserver.disconnect();
  }

  getWorkOrders(payload) {
    const obsDataOnGrid = this._clientWO.getGridColumnsStream(
      {
        contractName: payload.contractName,
        startDate: payload.startDateFilter,
        endDate: payload.endDateFilter,
      },
      payload.GridState,
      payload.ColumnsConfig,
      payload.installDateOption
    );
  }

  decodeFilterValue(input: string): string {
    let decoded = input;
    try {
      while (decoded !== decodeURIComponent(decoded)) {
        decoded = decodeURIComponent(decoded);
      }
      return decoded;
    } catch {
      return decoded;
    }
  }
  updateGridState(state: State) {
    // update home grid state
    this.updatLocalGridState(state);

    // update active grid state.
    let filters = state.filter.filters.map(
      (item: CompositeFilterDescriptor) => {
        const filters = [...item.filters];
        const value = filters.map((filterInnerItem: FilterDescriptor) => {
          return typeof filterInnerItem.value === 'string' ||
            typeof filterInnerItem.value === 'object'
            ? this.decodeFilterValue(filterInnerItem.value)
            : filterInnerItem.value.map((item) => this.decodeFilterValue(item));
        });
        const operator = filters.map((filterInnerItem: FilterDescriptor) => {
          return filterInnerItem.operator;
        });

        const field = item.filters.map((filterInnerItem: FilterDescriptor) => {
          return filterInnerItem.field;
        });
        const payload = {
          field: field.length > 0 ? field[0] : undefined,
          value,
          operator: operator.length > 0 ? operator[0] : undefined,
        };
        return {
          ...payload,
        };
      }
    );
    // Filter StatusWeb; StatusWeb filter is handled differently and cause issues in getting data
    filters = filters.filter((item) => item.field !== 'StatusWeb');
    const currentFilters = this.gridStateMap().filter
      ? this.gridStateMap().filter.filters
      : [];
    const newState: any = {
      ...state,
      filter: {
        ...state.filter,
        filters: [...currentFilters, ...filters],
      },
    };
    const activeView = {
      ...this.clientViewConf.getCurrentActiveView(),
      GridState: newState,
    };
    this.updateCurrentState(activeView);
  }

  updatLocalGridState(state: State) {
    // update home grid state
    this.gridState.set(state);
  }
  updateWOSelection(selection: WorkOrderBase[]) {
    this._clientWO.RXselectedWOIDsShared.next(selection);
  }
  updateColumnsList(columnsList: ColumnSettings[]) {
    const currentCols = this.columnsList();
    // check if we need to call backend to get the new cols data

    // we only need to check cols if isJson true
    // filter the list and remove when isJson is false
    // remove hidden cols
    // we don't need to call backend when cols are hidden
    const filteredColsList = columnsList.filter(
      (col) => !col.hidden && col.isJson
    );

    const newVisibleCols = filteredColsList.filter((newCol) => {
      const match = currentCols.find((col) => col.field === newCol.field);
      return match && match.hidden === true && newCol.hidden === false;
    });
    const currentView = this.clientViewConf.getCurrentActiveView();

    const activeView = {
      ...currentView,
      ColumnsConfig: columnsList, // get current column state
    };

    this.updateCurrentState(activeView);
    this.columnsList.set(columnsList);
    if (
      newVisibleCols.length > 0 ||
      (this.canFilterColsOnChange === false && filteredColsList.length > 0)
    ) {
      // this is to skip the cols filters logic on a initial change
      this.canFilterColsOnChange = true;
      // Initial saved grid state from server
      const gridState = this.gridStateMap();
      // get new colmumns
      this._clientWO.getGridColumnsData(
        {
          contractName: activeView.contractName,
          startDate: activeView.startDateFilter,
          endDate: activeView.endDateFilter,
        },
        gridState,
        activeView.ColumnsConfig,
        activeView.installDateOption
      );
    }
  }
  onPanelMapCollapse(e) {
    if (e.item.title == 'Maps' && e.item.expanded == true)
      //only on Collapse of Map panel we save the prev selected tool
      this.selectMapTool.set(this._mapSharedUtilService.prevMode);
  }

  updateCurrentState(activeView) {
    this.clientViewConf.RXSetCurrentActiveView(activeView.name, false, [
      activeView,
    ]);
  }

  updateShowItem(showItem) {
    this.showItem.set(showItem);
  }
  clearFilters() {
    this.updateGridState(this.initialGridState);
  }
  logout() {
    this.authService.logout();
  }

  onLanguageChange(language: string) {
    this.languageService.setLanguage(language);
  }
}